<template>
  <div class="content__body" ref="scrollContent">
    <content-box class="content__my-info" :width="'800px'">
      <div class="title--wrap">
        <i class="icon icon--person-size50"></i>
        <p class="title" v-html="$t('userInfo.myInformation.default.title.changeInfo')"></p>
      </div>
      <div class="form--wrap">
        <form>
          <fieldset>
            <div class="input__id">
              <label for="userId">{{ $t('common.id') }}</label>
              <input id="userId" type="text" name="user-id"
                     readonly
                     :value="userId">
            </div>
            <div class="input__user-name">
              <label for="email">{{ $t('common.email') }}</label>
              <input id="email" type="text" name="email"
                     readonly
                     :value="email">
            </div>
            <div class="input__user-name">
              <label for="userName">{{ $t('common.corporation') }}</label>
              <input id="userName" type="text" name="user-name"
                     autocomplete="user-name"
                     v-model.trim="model.branchName"
                     :placeholder="$t('common.placeHolder.enterCorporation')"
                     :class="{error: modelError.branchName}"
                     @focus="modelError.branchName = ''">
              <strong v-if="modelError.branchName" class="validation-text">
                {{ $t(`common.validationErrorText.${modelError.branchName}`) }}</strong>
            </div>
            <div class="input__email-lang" v-show="false">
              <label for="emailLang">
                {{ $t('common.emailLang') }}
                <ui-icon-tooltip iconSize="white-size17" arrowBottom arrowLeft>
                  <p class="desc">{{ $t('account.signUp.try.form.emailLang.desc') }}</p>
                </ui-icon-tooltip>
              </label>
              <ui-selectbox
                id="emailLang"
                width="100%"
                v-model="model.displayLang"
                :items="languageList" />
            </div>
            <div class="input__list">
              <label>{{ $t('common.countryCode') }}</label>
              <ul class="site-code-list">
                <li v-if="siteCodeList.includes('ALL')">ALL</li>
                <li v-else v-for="code in originalSiteCodeList" v-bind:key="code">
                  {{code}} <span v-if="countryCodeList[code]">[{{countryCodeList[code]}}]</span>
                </li>
              </ul>
            </div>
            <div class="input__timezone">
              <label for="timezone">
                {{ $t('common.timezone') }}
                <ui-icon-tooltip iconSize="white-size17" arrowBottom arrowLeft>
                  <p class="desc">{{ $t('account.signUp.try.form.timezone.desc') }}</p>
                </ui-icon-tooltip>
              </label>
              <timezone-selectbox id="timezone"
                                  v-model="model.timezoneName"
                                  :width="530"
                                  @change="selectedTimezone" />
            </div>
          </fieldset>
        </form>
      </div>
    </content-box>
    <content-box class="content__password" :width="'800px'">
      <div class="title--wrap">
        <i class="icon icon--lock-size50"></i>
        <p class="title" v-html="$t('userInfo.myInformation.default.title.changePassword')"></p>
      </div>
      <div class="form--wrap">
        <form>
          <fieldset>
            <input type="text" hidden disabled autocomplete="off">
            <div class="input__current-password">
              <label for="currentPassword">{{ $t('common.currentPassword') }}</label>
              <input id="currentPassword" type="password" name="current-password"
                     v-model.trim="model.userPw"
                     autocomplete="current-password"
                     :placeholder="$t('common.placeHolder.enterPassword')"
                     :class="{error: modelError.userPw}"
                     @focus="modelError.userPw = ''">
              <i class="icon icon--pw-eye"
                 :class="{on: !toggleInputType}"
                 @click="toggleShowPasswordValue"></i>
              <strong v-if="modelError.userPw" class="validation-text">
                {{ $t(`common.validationErrorText.${modelError.userPw}`) }}</strong>
            </div>
            <div class="input__new-password">
              <label for="newPassword">{{ $t('common.newPassword') }}</label>
              <input id="newPassword" type="password" name="new-password"
                     autocomplete="new-password"
                     v-model.trim="model.userNewPw"
                     :placeholder="$t('common.placeHolder.enter6char')"
                     :class="{error: modelError.userNewPw}"
                     @focus="modelError.userNewPw = ''">
              <i class="icon icon--pw-eye"
                 :class="{on: !toggleInputTypeNewPw}"
                 @click="toggleShowNewPasswordValue"></i>
              <strong v-if="modelError.userNewPw" class="validation-text">
                {{ $t(`common.validationErrorText.${modelError.userNewPw}`) }}</strong>
            </div>
          </fieldset>
        </form>
      </div>
    </content-box>
    <div class="button--wrap">
      <UiButton @click="onMoveDashboard">{{ $t('common.cancel') }}</UiButton>
      <UiButton class="btn__modify" :primary="true" @click="onModify">{{ $t('common.save') }}</UiButton>
    </div>
    <p class="text-withdraw" v-if="!userData.isGmc">
      <strong @click="applyWithdraw">{{ $t('userInfo.myInformation.default.withdraw') }}</strong>
    </p>
    <back-to-top :refName="'scrollContent'" />
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import { getUserInfo, modify } from '@/api/user'
import ContentBox from '@/components/ui/ContentBox'
import UiButton from '@/components/ui/UiButton'
import BackToTop from '@/components/ui/BackToTop'
import TimezoneSelectbox from '@/components/ui/TimezoneSelectbox'
import countryCode from '@/data/country-code'
import UiSelectbox from '@/components/ui/UiSelectbox'
import UiIconTooltip from '@/components/ui/UiIconTooltip'
import AppDialog from '@/components/ui/AppDialog'
import { create } from 'vue-modal-dialogs'

const openDialog = create(AppDialog)

export default {
  components: {
    ContentBox,
    UiButton,
    BackToTop,
    TimezoneSelectbox,
    UiSelectbox,
    UiIconTooltip
  },
  data () {
    return {
      userId: null,
      email: null,
      siteCodeList: [],
      originalSiteCodeList: [],
      browserLang: '',
      languageList: [
        {
          value: 'ko',
          text: '한국어'
        },
        {
          value: 'en',
          text: 'English'
        },
        {
          value: 'ja',
          text: '日本語'
        }
      ],
      model: {
        branchName: null,
        timezone: null,
        timezoneName: null,
        userPw: '',
        userNewPw: ''
      },
      modelError: {
        branchName: null,
        userPw: null,
        userNewPw: null
      },
      toggleInputType: true,
      toggleInputTypeNewPw: true
    }
  },
  computed: {
    ...mapState([
      'userData'
    ]),
    countryCodeList () {
      let items = {}
      Object.entries(countryCode).map(([key, value]) => {
        Object.entries(value.country).map(([k, v]) => {
          items[k] = v
        })
      })
      return items
    }
  },
  methods: {
    ...mapActions(['storeUserData']),
    applyWithdraw () {
      this.$emit('applyWithdraw')
    },
    onMoveDashboard () {
      this.$router.push({ name: 'dashboard' })
    },
    selectedTimezone (value) {
      this.model.timezone = value.offset
      this.model.timezoneName = value.timezone
    },
    toggleShowPasswordValue () {
      const targetElement = document.querySelector('#currentPassword')
      if (targetElement.attributes.type.value === 'password') {
        targetElement.setAttribute('type', 'text')
        this.toggleInputType = false
      } else {
        targetElement.setAttribute('type', 'password')
        this.toggleInputType = true
      }
    },
    toggleShowNewPasswordValue () {
      const targetElement = document.querySelector('#newPassword')
      if (targetElement.attributes.type.value === 'password') {
        targetElement.setAttribute('type', 'text')
        this.toggleInputTypeNewPw = false
      } else {
        targetElement.setAttribute('type', 'password')
        this.toggleInputTypeNewPw = true
      }
    },
    onModify () {
      let allPass = true

      if (!this.model.branchName) {
        this.modelError.branchName = 'requiredCorporation'
        allPass = false
      }

      if (this.model.userPw || this.model.userNewPw) {
        if (!this.model.userPw) {
          this.modelError.userPw = 'requiredPassword'
          allPass = false
        }
        if (!this.model.userNewPw) {
          this.modelError.userNewPw = 'requiredPassword'
          allPass = false
        } else if (this.model.userNewPw.length < 6) {
          this.modelError.userNewPw = 'passwordInsufficient'
          allPass = false
        }
      }

      if (allPass) {
        let param = {
          ...this.model
        }
        modify(param).then(() => {
          openDialog({
            dialogType: 'userInfo.myInformationEditSuccess'
          }).then(() => {
            this.model.userPw = ''
            this.model.userNewPw = ''
          })
          this.storeUserData()
          this.$forceUpdate()
        }).catch(err => {
          if (err.message === 'Wrong password') {
            this.modelError.userPw = 'wrongPassword'
          }
        })
      }
    }
  },
  created () {
    let userLanguage = navigator.language
    userLanguage = userLanguage.substring(0, 2)
    this.browserLang = userLanguage

    getUserInfo().then(response => {
      this.userId = response.userId
      this.model.userName = response.userName
      this.model.branchName = response.branchName
      this.email = response.email
      this.siteCodeList = response.siteCodeList
      this.originalSiteCodeList = response.originalSiteCodeList
      this.model.timezone = response.timezone
      this.model.timezoneName = response.timezoneName
    })
  }
}
</script>

<style lang="scss" scoped="scoped">
  .content__body {
    height: calc(100vh - 166px);
    padding: 30px 30px 0;
    overflow-y: auto;

    & > [class*=content__] {
      position: relative;
      padding: 50px 40px 50px 230px;
      box-sizing: border-box;
      margin: 0 auto;

      .title--wrap {
        position: absolute;
        top: 40px;
        left: 40px;

        .title {
          font-size: 15px;
          font-weight: bold;
          line-height: 1.47;
          color: #2f2f2f;
          margin-top: 14px;
        }
      }

      .form--wrap {
        [class*=input__] {
          position: relative;
          margin-top: 40px;

          &.input__id,
          &.input__current-password {
            margin-top: 0;
          }
          &.input__current-password,
          &.input__new-password {
            input:focus {
              & + .bubble {
                opacity: 1;
              }
            }
            .icon--pw-eye {
              position: absolute;
              top: 19px;
              right: 5px;
              padding: 5px;
            }
            .bubble {
              opacity: 0;
              width: 250px;
              position: absolute;
              top: 0;
              left: 50%;
              transform: translate(-50%, -100%);

              /deep/ span {
                white-space: normal;
                text-align: left;
              }
            }
          }

          &.input__current-password,
          &.input__new-password {
            width: 350px;
          }

          &.input__email-lang {
            label {
              margin-bottom: 5px;

              .icon--info-white-size17 {
                vertical-align: middle;
              }
            }
          }

          &.input__timezone {
            position: relative;

            label {
              margin-bottom: 5px;

              .icon--info-white-size17 {
                vertical-align: middle;
              }
            }
          }

          .site-code-list {
            height: 142px;
            margin: 5px 0 15px 0;
            overflow-y: auto;
            border: 1px solid #d7d7d7;
            background: #e6e6e6;
            border-radius: 5px;
            padding: 8px 0;

            li {
              @include textEllipsis;
              font-size: 13px;
              float: left;
              width: 50%;
              padding: 8px 20px;
              box-sizing: border-box;
              color: #2f2f2f;
            }
          }

          label {
            display: block;
            font-size: 13px;
            font-weight: bold;
            line-height: 1;
            color: #767676;
          }

          input[type=text],
          input[type=password] {
            margin-top: 5px;
            height: 38px;
            border-radius: 6px;
            border: solid 1px #bbbbbb;
            background-color: #ffffff;
            padding: 12px 35px 12px 12px;
            box-sizing: border-box;
            font-size: 14px;
            color: #2f2f2f;
            width: 100%;

            &.error {
              border-color: $errorColor;
            }
          }

          input[readonly] {
            border: solid 1px #d7d7d7;
            background-color: #e6e6e6;
            cursor: not-allowed;
          }

          .validation-text {
            @include validErrorText;
            position: absolute;
            left: 6px;
            bottom: -17px;
          }
        }
      }
    }

    .content__password {
      margin-top: 30px;
    }

    .button--wrap {
      margin-top: 30px;
      text-align: center;

      .btn__modify {
        margin-left: 5px;
      }
    }

    .text-withdraw {
      margin-top: 30px;
      font-size: 14px;
      font-weight: bold;
      color: #767676;
      text-align: center;

      strong {
        color: $brandColor;
        cursor: pointer;
      }
    }
  }
</style>
